const FONT_FIX_STYLE_ID = 'wx-font-fix-style'
const FONT_FIX_TARGET_PX = 15

const getGlobalWindow = () => window as any

const getProbeFontSize = (fontSize: string) => {
  const mountTarget = document.body || document.documentElement
  if (!mountTarget) return 0

  const probe = document.createElement('div')
  probe.style.cssText = `position:absolute;left:-9999px;top:-9999px;visibility:hidden;pointer-events:none;line-height:1;font-size:${fontSize};`
  probe.textContent = 'M'
  mountTarget.appendChild(probe)
  const computedSize = parseFloat(window.getComputedStyle(probe).getPropertyValue('font-size')) || 0
  probe.parentNode?.removeChild(probe)
  return computedSize
}

const ensureStyleTag = () => {
  let styleTag = document.getElementById(FONT_FIX_STYLE_ID) as HTMLStyleElement | null
  if (styleTag) return styleTag

  styleTag = document.createElement('style')
  styleTag.id = FONT_FIX_STYLE_ID
  ;(document.head || document.documentElement).appendChild(styleTag)
  return styleTag
}

const getTextScaleFactor = () => {
  const scaledFontSize = getProbeFontSize('10px')
  if (!scaledFontSize) return 1
  return 10 / scaledFontSize
}

const applyFixedRootFontSize = (targetRootFontSize: number) => {
  const styleTag = ensureStyleTag()
  const fontSizeValue = `${targetRootFontSize}px`
  styleTag.textContent = `
html {
  --font-size: ${fontSizeValue} !important;
  --wx-design-root-font-size: ${fontSizeValue} !important;
  font-size: ${fontSizeValue} !important;
}
body {
  font-size: ${fontSizeValue} !important;
}
  `.trim()
}

const forceFontReflow = () => {
  void document.documentElement.offsetWidth
  void document.documentElement.offsetHeight
  if (document.body) {
    void document.body.offsetWidth
    void document.body.offsetHeight
  }
}

export const installWeChatFontFix = () => {
  if (!import.meta.client) return

  const globalWindow = getGlobalWindow()
  if (globalWindow.__WECHAT_FONT_FIX_INSTALLED__) return
  globalWindow.__WECHAT_FONT_FIX_INSTALLED__ = true

  const isAndroidWeChat = /Android/i.test(navigator.userAgent) && /MicroMessenger/i.test(navigator.userAgent)
  if (!isAndroidWeChat) return

  const fixAndroidRootFontSize = (reason: string) => {
    const scaleFactor = getTextScaleFactor()
    const currentRootFontSize =
      parseFloat(window.getComputedStyle(document.documentElement).getPropertyValue('font-size')) || 0
    if (!scaleFactor || !currentRootFontSize) return

    if (!globalWindow.__WX_DESIGN_ROOT_FONT_SIZE__ || globalWindow.__WX_DESIGN_ROOT_FONT_SIZE__ < 1) {
      globalWindow.__WX_DESIGN_ROOT_FONT_SIZE__ = currentRootFontSize * scaleFactor || FONT_FIX_TARGET_PX
    }

    globalWindow.__WX_LAST_FONT_FIX_REASON__ = reason
    globalWindow.__WX_LAST_TEXT_SCALE_FACTOR__ = scaleFactor
    applyFixedRootFontSize(globalWindow.__WX_DESIGN_ROOT_FONT_SIZE__ * scaleFactor)
  }

  const runAndroidFontFix = (reason: string) => {
    fixAndroidRootFontSize(reason)
    forceFontReflow()
  }

  // First pass neutralizes enlarged text metrics; the follow-up pass restores the design root size.
  const scheduleAndroidFontFix = (reason: string) => {
    runAndroidFontFix(`${reason}-now`)

    window.requestAnimationFrame(() => {
      runAndroidFontFix(`${reason}-raf1`)
      window.requestAnimationFrame(() => {
        runAndroidFontFix(`${reason}-raf2`)
      })
    })

    window.setTimeout(() => {
      runAndroidFontFix(`${reason}-timeout`)
    }, 120)
  }

  const lockWeChatFontScale = () => {
    const bridge = globalWindow.WeixinJSBridge
    if (!bridge || typeof bridge.invoke !== 'function') return

    bridge.invoke('setFontSizeCallback', { fontSize: '2' })
    scheduleAndroidFontFix('bridge-lock')

    if (typeof bridge.on === 'function') {
      bridge.on('menu:setfont', () => {
        bridge.invoke('setFontSizeCallback', { fontSize: '2' })
        scheduleAndroidFontFix('menu-setfont')
      })
    }
  }

  globalWindow.__scheduleWeChatFontFix__ = scheduleAndroidFontFix
  globalWindow.__runWeChatFontFix__ = runAndroidFontFix

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        scheduleAndroidFontFix('dom-content-loaded')
      },
      false
    )
  } else {
    scheduleAndroidFontFix('document-ready')
  }

  window.addEventListener('pageshow', () => scheduleAndroidFontFix('page-show'))

  if (typeof globalWindow.WeixinJSBridge === 'object' && globalWindow.WeixinJSBridge) {
    lockWeChatFontScale()
  } else {
    document.addEventListener('WeixinJSBridgeReady', lockWeChatFontScale, false)
  }
}
